import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";

function McqRow({ q, value, onPick }) {
  return (
    <div data-testid={`answer-row-${q.number}`} className={`flex items-center gap-2 rounded-xl border px-2.5 py-1.5 ${value ? "border-[#C4B5FD] bg-[#FAF9FE]" : "border-[#EEEAF8]"}`}>
      <span className="w-8 shrink-0 text-sm font-bold text-[#1E1B2E]">{q.number}</span>
      <div className="flex flex-wrap gap-1">
        {q.options.map((o) => {
          const sel = value === o.key;
          return (
            <button key={o.key} data-testid={`sheet-${q.number}-${o.key}`} onClick={() => onPick(sel ? undefined : o.key)}
              className={`grid h-8 w-8 place-items-center rounded-lg border-2 text-xs font-bold transition-all ${sel ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-[#E9E5F5] text-[#7C3AED] hover:border-[#C4B5FD]"}`}>{o.key}</button>
          );
        })}
      </div>
    </div>
  );
}

function SaqBox({ q, value, onChange }) {
  return (
    <div data-testid={`saq-row-${q.number}`} className="rounded-xl border border-[#EEEAF8] p-2.5">
      <div className="mb-1 flex justify-between text-sm font-bold text-[#1E1B2E]"><span>Q{q.number}{q.label && q.label !== `Q${q.number}` ? ` · ${q.label}` : ""}</span><span className="text-xs text-[#948EAB]">{q.max_marks} marks</span></div>
      <textarea data-testid={`saq-input-${q.number}`} value={value || ""} onChange={(e) => onChange(e.target.value)}
        placeholder="Type your answer…" className="min-h-[110px] w-full rounded-lg border border-[#E9E5F5] p-2 text-sm outline-none focus:border-[#C4B5FD]" />
    </div>
  );
}

function ExtendedBox({ q, value, onChange }) {
  const words = (value || "").trim() ? value.trim().split(/\s+/).length : 0;
  return (
    <div data-testid={`ext-row-${q.number}`} className="rounded-xl border border-[#EEEAF8] p-2.5">
      <div className="mb-1 flex justify-between text-sm font-bold text-[#1E1B2E]"><span>Q{q.number} · {q.label || "Extended response"}</span><span className="text-xs text-[#948EAB]">/40</span></div>
      <textarea data-testid={`ext-input-${q.number}`} value={value || ""} onChange={(e) => onChange(e.target.value)}
        placeholder="Write your response…" className="min-h-[45vh] w-full rounded-lg border border-[#E9E5F5] p-3 text-sm leading-relaxed outline-none focus:border-[#C4B5FD]" />
      <div className={`mt-1 text-right text-xs font-semibold ${q.min_words && words < q.min_words ? "text-amber-600" : "text-[#948EAB]"}`} data-testid={`ext-wordcount-${q.number}`}>
        {words} words{q.min_words ? ` · minimum ${q.min_words}` : ""}
      </div>
    </div>
  );
}

export function MixedSheet({ hw, answers, setAnswers, onFinish }) {
  const [part, setPart] = useState(0);
  const parts = hw.parts || [];
  const qs = hw.questions.filter((q) => q.part === part);
  const done = (i) => hw.questions.filter((q) => q.part === i && answers[q.id]).length;
  const size = (i) => hw.questions.filter((q) => q.part === i).length;
  const last = part === parts.length - 1;
  const set = (id, v) => setAnswers({ ...answers, [id]: v });
  return (
    <div data-testid="mixed-sheet">
      <div className="mb-3 flex flex-wrap gap-1.5">
        {parts.map((p, i) => (
          <button key={i} onClick={() => setPart(i)} data-testid={`part-tab-${i}`}
            className={`rounded-lg px-2.5 py-1 text-xs font-bold transition-colors ${i === part ? "bg-[#4B2BB8] text-white" : "bg-[#F3F0FF] text-[#4B2BB8] hover:bg-[#EAE4FF]"}`}>
            Part {i + 1} · {done(i)}/{size(i)}
          </button>
        ))}
      </div>
      <h3 className="mb-1 font-heading font-bold text-[#1E1B2E]" data-testid="part-title">{parts[part]?.title}</h3>
      <p className="mb-3 text-xs text-[#948EAB]">Questions {parts[part]?.start}–{parts[part]?.end}. You can come back and change answers until you submit the exam.</p>
      <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
        {qs.map((q) => q.type === "saq"
          ? <SaqBox key={q.id} q={q} value={answers[q.id]} onChange={(v) => set(q.id, v)} />
          : q.type === "extended"
          ? <ExtendedBox key={q.id} q={q} value={answers[q.id]} onChange={(v) => set(q.id, v)} />
          : <McqRow key={q.id} q={q} value={answers[q.id]} onPick={(v) => set(q.id, v)} />)}
      </div>
      <div className="mt-4 flex gap-2">
        {part > 0 && <Button variant="outline" onClick={() => setPart(part - 1)} data-testid="part-prev" className="h-11 rounded-xl"><ChevronLeft className="h-4 w-4" /></Button>}
        {last
          ? <Button onClick={onFinish} data-testid="part-submit-exam" className="h-11 flex-1 rounded-xl ember-gradient font-bold"><CheckCircle2 className="mr-1.5 h-4 w-4" /> Submit exam</Button>
          : <Button onClick={() => setPart(part + 1)} data-testid="part-next" className="h-11 flex-1 rounded-xl ember-gradient font-bold">Submit part &amp; continue <ChevronRight className="ml-1 h-4 w-4" /></Button>}
      </div>
    </div>
  );
}
